import get from 'lodash/get';
import { getOnlineOrderingUrlParams } from 'bento-ordering/utils/url';

import { setupInitialData } from 'bento-ordering/base/actions/setupInitialData';
import handleSingleLocationFlow from 'bento-ordering/base/actions/handleSingleLocationFlow';
import { validateReferralSource } from 'bento-ordering/base/utils/validateReferralSource';
import { validateUTMCampaign } from 'bento-ordering/base/utils/validateUTMCampaign';
import GroupOrderNotFoundError from 'bento-ordering/group_ordering/errors/GroupOrderNotFoundError';
import { editLocationInCart } from 'bento-ordering/cart/actions';
import { ALL_ONLINE_ORDERING_FULFILLMENT_METHODS } from 'constants/fulfillmentOptions';
import { openNotificationModal } from 'bento-ordering/base/actions/notificationModals';
import {
  CART_NOT_FOUND,
  FULFILLMENT_OPTION_NOT_AVAILABLE
} from 'bento-ordering/cart/constants/notificationTypes';

/*
  This thunk is dispatched at the beginning of app render.
  We setup initial data (cart, locations, account, user), then, if there is more than 1
  location in the response and the requested route is root or /location-slug,
  fire handleSingleLocation flow which will navigate the user to menu page or order settings page
*/

export const setupInitialFlow = ({ resetCart = false }) => async (
  dispatch,
  getState
) => {
  const { router } = getState();
  const { isRoot, locationSlug, isLocationPage } = getOnlineOrderingUrlParams(
    router.location.pathname
  );

  const {
    utm_source: utmSource,
    utm_campaign: utmCampaign,
    promo,
    group_order: groupOrderParam = '',
    seatIdValue = '',
    fulfillment: fulfillmentParam = ''
  } = router.location.query;

  const validReferralSource = validateReferralSource(utmSource);
  const validUTMCampaign = validateUTMCampaign(utmCampaign);

  const result = await dispatch(
    setupInitialData({
      locationSlug,
      resetCart,
      referralSource: validReferralSource,
      utmCampaign: validUTMCampaign,
      promo,
      groupOrderParam,
      seatIdValue
    })
  );

  /**
   * We are currently assuming that if a group order query param is present
   * and the result from the initial data endpoint is a 404 (not found)
   * it means that the group order could not being found and then we throw an error.
   */
  if (groupOrderParam && result?.payload?.status === 404) {
    throw new GroupOrderNotFoundError();
  }

  const locations = get(result, 'payload.locations', []);
  const isSingleLocation = locations.length === 1;
  if (isSingleLocation && (isRoot || isLocationPage)) {
    await dispatch(handleSingleLocationFlow(result.payload.cart));
  }

  if (
    fulfillmentParam &&
    ALL_ONLINE_ORDERING_FULFILLMENT_METHODS.includes(fulfillmentParam)
  ) {
    const { cart } = getState();

    if (cart.online_ordering_fulfillment_method !== fulfillmentParam) {
      const response = await dispatch(
        editLocationInCart({
          online_ordering_location: cart.online_ordering_location,
          online_ordering_fulfillment_method: fulfillmentParam
        })
      );

      if (response.payload.status === 404) {
        dispatch(openNotificationModal(CART_NOT_FOUND));
      } else if (
        response.payload.status === 400 &&
        response.payload.response.error ===
          'Fulfillment options have changed. Please refresh the page to continue.'
      ) {
        dispatch(openNotificationModal(FULFILLMENT_OPTION_NOT_AVAILABLE));
      }
    }
  }
};
